// The per-frame uniform arena (the runtime design §2).
//
// WebGL2 writes a uniform straight into the program object, so a setter
// before a draw is the value that draw reads. WebGPU has no such store: the
// block is ONE buffer, and a draw reads the slice the bind group points at.
// A frame that draws forty passes needs forty slices.
//
// So every draw copies the pipeline's CPU shadow into this arena and takes
// the byte offset back. The offset goes into the bind group as a DYNAMIC
// offset, and the whole arena reaches the GPU as ONE `writeBuffer` before the
// frame is submitted. That keeps the per-draw cost at a memory copy.
//
// Two rules hold the design together:
//
//  1. **Every slice starts at a multiple of 256 bytes.** That is the widest
//     `minUniformBufferOffsetAlignment` the specification allows, so the
//     arena never asks the device what its own limit is.
//  2. **An overflow drops the draw, and never the frame.** The arena doubles
//     for the NEXT frame instead, because the buffer a draw already reads
//     must not be replaced while the frame is open.

import { BUFFER_COPY_DST, BUFFER_UNIFORM } from './flags';

/** The alignment of one slice. It is the specification's widest limit. */
export const SLOT_BYTES = 256;

/** How many slices the arena starts with: 1024 slots, 256 KB. */
export const START_SLOTS = 1024;

/** The next multiple of the slot size at or above `bytes`. */
export function alignSlot(bytes: number): number {
  return Math.ceil(Math.max(1, bytes) / SLOT_BYTES) * SLOT_BYTES;
}

export class UniformArena {
  /** Bumped whenever the buffer is replaced, so a bind group is rebuilt. */
  generation = 0;

  private device: GPUDevice | null = null;
  private gpu: GPUBuffer | null = null;
  private staging: Uint8Array;
  /** How many bytes the arena holds now, and how many the next frame gets. */
  private bytes: number;
  private want: number;
  private at = 0;
  /** An overflow is said ONCE, because it would say it every draw. */
  private said = false;

  constructor(slots: number = START_SLOTS) {
    this.bytes = Math.max(1, slots) * SLOT_BYTES;
    this.want = this.bytes;
    this.staging = new Uint8Array(this.bytes);
  }

  /** The live buffer, or null before `create` and after a loss. */
  get buffer(): GPUBuffer | null { return this.gpu; }

  /** How many bytes the arena holds. A bind group's slice cannot pass it. */
  get size(): number { return this.bytes; }

  /** How many bytes the frame has taken so far. The test reads it. */
  get used(): number { return this.at; }

  create(device: GPUDevice): void {
    this.destroy();
    this.device = device;
    this.gpu = device.createBuffer({
      label: 'fast: uniform arena',
      size: this.bytes,
      usage: BUFFER_UNIFORM | BUFFER_COPY_DST,
    });
    this.generation++;
  }

  destroy(): void {
    if (this.gpu) this.gpu.destroy();
    this.gpu = null;
  }

  /**
   * Opens a frame.
   *
   * A frame that overflowed asks for twice the room, and the buffer is
   * replaced HERE and not at the overflow, because the open frame still
   * draws from the old one.
   */
  reset(): void {
    this.at = 0;
    if (this.want <= this.bytes) return;
    this.bytes = this.want;
    this.staging = new Uint8Array(this.bytes);
    if (this.device) this.create(this.device);
  }

  /**
   * Copies one block into the arena and gives its byte offset back.
   *
   * It answers -1 when the frame is full. The caller then drops the draw:
   * one frame of one pass is lost and the next frame has the room.
   */
  push(block: Uint8Array): number {
    const step = alignSlot(block.byteLength);
    if (this.at + step > this.bytes) {
      this.want = Math.max(this.want, this.bytes * 2);
      if (!this.said) {
        this.said = true;
        // eslint-disable-next-line no-console
        console.warn(`fast: the uniform arena is full at ${this.bytes} bytes; it doubles`);
      }
      return -1;
    }
    const at = this.at;
    this.staging.set(block, at);
    this.at = at + step;
    return at;
  }

  /** One write of everything the frame pushed. It runs before `submit`. */
  flush(queue: GPUQueue): void {
    if (!this.gpu || this.at === 0) return;
    queue.writeBuffer(this.gpu, 0, this.staging, 0, this.at);
  }
}
